/**
 * Retry utility with exponential backoff
 * Best practice for handling transient network errors
 */

export type RetryOptions = {
  maxRetries?: number;
  initialDelay?: number; // in milliseconds
  maxDelay?: number; // in milliseconds
  backoffMultiplier?: number;
  retryable?: (error: any) => boolean; // Function to determine if error is retryable
};

const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2,
  retryable: (error: any) => {
    // Retry on network errors, 5xx errors, and rate limits
    if (error?.message?.includes('NetworkError') || 
        error?.message?.includes('Failed to fetch') ||
        error?.message?.includes('timeout')) {
      return true;
    }
    
    // Retry on HTTP 429 (rate limit), 500, 502, 503, 504
    if (error?.status === 429 || 
        error?.status === 500 || 
        error?.status === 502 || 
        error?.status === 503 || 
        error?.status === 504) {
      return true;
    }
    
    return false;
  },
};

/**
 * Retry a function with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  let delay = opts.initialDelay;
  let lastError: any;

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Don't retry if this is the last attempt or error is not retryable
      if (attempt === opts.maxRetries || !opts.retryable(error)) {
        throw error;
      }

      // Wait before retrying (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Increase delay for next retry (capped at maxDelay)
      delay = Math.min(delay * opts.backoffMultiplier, opts.maxDelay);
    }
  }

  throw lastError;
}

/**
 * Create a user-friendly error message from an error
 */
export function getUserFriendlyErrorMessage(error: any): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error?.message) {
    const message = error.message;

    // Network errors
    if (message.includes('NetworkError') || message.includes('Failed to fetch')) {
      return 'Unable to connect. Please check your internet connection and try again.';
    }

    if (message.includes('timeout')) {
      return 'Request timed out. Please try again.';
    }

    // HTTP errors
    if (error?.status === 401 || message.includes('401') || message.includes('Unauthorized')) {
      return 'Authentication failed. Please check your configuration.';
    }

    if (error?.status === 403 || message.includes('403') || message.includes('Forbidden')) {
      return 'Access denied. Please check your permissions.';
    }

    if (error?.status === 404 || message.includes('404') || message.includes('Not Found')) {
      return 'Resource not found.';
    }

    if (error?.status === 429 || message.includes('429') || message.includes('rate limit')) {
      return 'Too many requests. Please wait a moment and try again.';
    }

    if (error?.status >= 500) {
      return 'Server error. Please try again later.';
    }

    // Return the original message if it's user-friendly
    return message;
  }

  return 'An unexpected error occurred. Please try again.';
}
